var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { ParzivalGatewayAuthError } from 'src/utils/auth';
/** Start conservative for mobile networks + cold starts; tune down from the `COMPLIANCE_*_API_LATENCY` p99. */
export const COMPLIANCE_TIMEOUT_MS = {
    HIGH_PRIORITY: 5000,
    STANDARD_PRIORITY: 8000,
    TOP_HIGHLIGHTS: 8000,
    // The backend can make two capped calls in sequence for detail content.
    DETAIL_CONTENT: 10000,
};
const classifyFailure = (e, signal) => {
    // Some browsers reject with AbortError even when the timeout fired, so the signal's reason wins over the error name.
    if (signal.aborted) {
        const timedOut = signal.reason instanceof DOMException && signal.reason.name === 'TimeoutError';
        return { status: timedOut ? 'timeout' : 'cancelled' };
    }
    const cause = e instanceof FetchError ? e.cause : e;
    const causeName = cause === null || cause === void 0 ? void 0 : cause.name;
    if (causeName === 'TimeoutError')
        return { status: 'timeout' };
    if (causeName === 'AbortError')
        return { status: 'cancelled' };
    if (cause instanceof ParzivalGatewayAuthError)
        return { status: 'http-error', httpStatus: cause.status };
    // The interceptor throws HTTP errors as plain DefaultError objects, which the client does not wrap in FetchError.
    const httpStatus = e === null || e === void 0 ? void 0 : e.status;
    if (!(e instanceof Error) && typeof httpStatus === 'number')
        return { status: 'http-error', httpStatus };
    if (cause instanceof TypeError)
        return { status: 'network-unavailable' };
    return { status: 'error' };
};
const describeFailure = (e) => {
    var _a, _b;
    if (e instanceof FetchError) {
        return { errorTitle: e.cause.name, errorDetails: `${e.cause.name}: ${e.cause.message}` };
    }
    const error = e;
    return { errorTitle: error === null || error === void 0 ? void 0 : error.title, errorDetails: (_b = (_a = error === null || error === void 0 ? void 0 : error.detail) !== null && _a !== void 0 ? _a : error === null || error === void 0 ? void 0 : error.title) !== null && _b !== void 0 ? _b : String(error) };
};
/**
 * Runs a compliance API request with a client-side timeout and records one `<prefix>_API_LATENCY` event,
 * plus `<prefix>_API_ERROR` for every failure except `cancelled`. Rethrows the original error.
 */
export const runComplianceRequest = (_a) => __awaiter(void 0, [_a], void 0, function* ({ eventPrefix, asin, timeoutMs, querySignal, rumClient, request, }) {
    // Without these APIs the request still runs and is recorded, just without the client timeout.
    const supportsTimeout = typeof AbortSignal.any === 'function' && typeof AbortSignal.timeout === 'function';
    const signal = supportsTimeout ? AbortSignal.any([querySignal, AbortSignal.timeout(timeoutMs)]) : querySignal;
    const start = Date.now();
    try {
        const result = yield request(signal);
        rumClient.recordEvent(`${eventPrefix}_API_LATENCY`, { asin, durationMs: Date.now() - start, status: 'success' });
        return result;
    }
    catch (e) {
        const classification = classifyFailure(e, signal);
        rumClient.recordEvent(`${eventPrefix}_API_LATENCY`, Object.assign({ asin, durationMs: Date.now() - start }, classification));
        if (classification.status !== 'cancelled') {
            rumClient.recordEvent(`${eventPrefix}_API_ERROR`, Object.assign({ asin }, describeFailure(e)));
        }
        throw e;
    }
});
